import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { Columns2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SecondarySidebarContext } from './secondary-sidebar-context';

export interface SecondarySidebarItem {
	label: string;
	path: string;
}

export interface SecondarySidebarGroup {
	heading: string;
	items: SecondarySidebarItem[];
}

interface SecondarySidebarLayoutProps {
	title: string;
	items?: SecondarySidebarItem[];
	groups?: SecondarySidebarGroup[];
	/** Routes that need the full width, so the sidebar starts collapsed there. */
	autoCollapsePaths?: string[];
	/** Rendered above the routed content, for a note that applies to every page here. */
	banner?: ReactNode;
	/** Draws a line between groups. */
	groupDividers?: boolean;
}

export default function SecondarySidebarLayout({
	title,
	items = [],
	groups,
	autoCollapsePaths = [],
	banner,
	groupDividers = false
}: SecondarySidebarLayoutProps) {
	const location = useLocation();

	const wantsCollapse = autoCollapsePaths.some(
		(path) => location.pathname === path || location.pathname.startsWith(path + '/')
	);
	const [collapsed, setCollapsed] = useState(wantsCollapse);
	const [pageHasToggle, setPageHasToggle] = useState(false);
	const expand = useCallback(() => setCollapsed(false), []);
	const sidebar = useMemo(() => ({ collapsed, expand, setPageHasToggle }), [collapsed, expand]);
	const floatingToggle = collapsed && !pageHasToggle;

	// Only act on the crossing in or out of such a route. Reacting to every render
	// would slam the sidebar shut again the moment someone reopened it.
	const wasCollapsing = useRef(wantsCollapse);
	useEffect(() => {
		if (wasCollapsing.current === wantsCollapse) return;
		wasCollapsing.current = wantsCollapse;
		setCollapsed(wantsCollapse);
	}, [wantsCollapse]);

	// Flat list of all items (from groups or from items prop) for active-detection
	const allItems: SecondarySidebarItem[] = groups
		? groups.flatMap((g) => g.items)
		: items;

	const isItemActive = (item: SecondarySidebarItem) => {
		const currentPath = location.pathname;
		const isCurrentPathMatch = currentPath === item.path || currentPath.startsWith(item.path + '/');
		if (!isCurrentPathMatch) return false;

		return !allItems.some((candidate) => {
			if (candidate.path === item.path || !candidate.path.startsWith(item.path + '/')) return false;
			return currentPath === candidate.path || currentPath.startsWith(candidate.path + '/');
		});
	};

	const renderItem = (item: SecondarySidebarItem) => (
		<Link
			key={item.path}
			to={item.path}
			className={cn(
				'px-2 py-1.5 rounded-2lg text-body-sm transition-colors',
				isItemActive(item)
					? 'bg-white border border-gray-100 shadow-[0_0_8px_0_rgba(0,0,0,0.04)] text-ink font-normal'
					: 'text-gray-900 hover:bg-neutral-100 border border-transparent'
			)}
		>
			{item.label}
		</Link>
	);

	return (
		<div className='flex h-svh overflow-hidden'>
			<div className={cn('secondary-sidebar overflow-hidden shrink-0', collapsed ? 'w-0' : 'w-[200px]')}>
				<div className='w-[200px] h-full flex flex-col bg-gray-100/80 backdrop-blur-[6px] border-r border-gray-500 p-3 gap-8'>
					<div className='flex items-end justify-between pt-0.5 shrink-0'>
						<span className='text-base font-semibold text-black-400'>{title}</span>
						<button
							onClick={() => setCollapsed(true)}
							className='p-1.5 rounded-full hover:bg-neutral-200 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer'
							aria-label='Collapse sidebar'
						>
							<Columns2 className='size-4 text-gray-800' />
						</button>
					</div>
					<nav className={cn('flex flex-col', groupDividers ? 'gap-5' : 'gap-6')}>
						{groups ? (
							groups.map((group, index) => (
								<div
									key={group.heading}
									className={cn('flex flex-col gap-1', groupDividers && index > 0 && 'border-t border-gray-300 pt-5')}
								>
									<p className='px-2 text-xs font-medium tracking-tight text-gray-700 mb-0.5'>
										{group.heading}
									</p>
									{group.items.map(renderItem)}
								</div>
							))
						) : (
							<div className='flex flex-col gap-1'>
								{items.map(renderItem)}
							</div>
						)}
					</nav>
				</div>
			</div>

			<div className={cn('flex-1 relative overflow-auto', floatingToggle && 'pl-8')}>
				{floatingToggle && (
					<button
						onClick={expand}
						className='absolute top-4 left-2 z-10 p-1.5 rounded-full hover:bg-neutral-50 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer'
						aria-label='Expand sidebar'
					>
						<Columns2 className='size-4 text-gray-800' />
					</button>
				)}
				{banner}
				<SecondarySidebarContext.Provider value={sidebar}>
					<Outlet />
				</SecondarySidebarContext.Provider>
			</div>
		</div>
	);
}
